// ================================================================
// src/pages/admin/AdminFreeStudy.jsx
// ================================================================

import React, {
  useState, useEffect, useMemo,
} from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import './admin-books.css';
import '../../components/books/books-shared.css';

import {
  engine,
  BooksEngineProvider,
  useBooksEngine, useEngineReady,
  usePendingFreeStudyProjects, useAllFreeStudyProjects,
  useTeacherProfile,
  useVersionActions, useProjectActions,
  useDiff,
  PROJECT_STATE,
} from '../../hooks/useBooksEngine.js';

import dataEngine from '../../core/books/data.js';
import { formatPrice, toPersianDigits } from '../../freeStudy/core.js';
import { resolveImageUrl } from '../../data/freeStudyData.js';

import {
  Spinner, Badge, Chip, EmptyState, SkeletonLoader,
  ConfirmModal,
  VideoPreview, ProjectStateBadge,
} from '../../components/books/BooksSharedUI.jsx';

// ================================================================
// MAIN ENTRY
// ================================================================
export default function AdminFreeStudyPage() {
  const { user } = useAuth();
  if (!user || user.role !== 'admin') return <AccessDenied />;
  return (
    <BooksEngineProvider user={user}>
      <Inner />
    </BooksEngineProvider>
  );
}

function AccessDenied() {
  const navigate = useNavigate();
  return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 64, marginBottom: 20 }}>🔒</div>
      <h2>دسترسی محدود</h2>
      <p>این صفحه مخصوص مدیران است.</p>
      <button className="ab-btn ab-btn-primary" onClick={() => navigate('/AdminHome')}>
        بازگشت
      </button>
    </div>
  );
}

// ================================================================
// INNER
// ================================================================
function Inner() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { ready } = useEngineReady();

  const reviewId = searchParams.get('review');
  const pending = usePendingFreeStudyProjects();
  const all = useAllFreeStudyProjects();

  const [isDarkMode, setIsDarkMode] = useState(() => {
    try { return engine.getRaw('navbar2-drawer-dark') === '1'; }
    catch { return false; }
  });

  useEffect(() => {
    try { engine.setRaw('navbar2-drawer-dark', isDarkMode ? '1' : '0'); }
    catch {}
  }, [isDarkMode]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'navbar2-drawer-dark') setIsDarkMode(e.newValue === '1');
    };
    window.addEventListener('storage', handler);
    const interval = setInterval(() => {
      try {
        const cur = engine.getRaw('navbar2-drawer-dark') === '1';
        setIsDarkMode((prev) => (prev !== cur ? cur : prev));
      } catch {}
    }, 500);
    return () => {
      window.removeEventListener('storage', handler);
      clearInterval(interval);
    };
  }, []);

  if (!ready) {
    return (
      <div className={`ab-page ${isDarkMode ? 'dark-mode' : ''}`}>
        <main className="ab-main" style={{ padding: 24 }}>
          <SkeletonLoader variant="card" count={3} />
        </main>
      </div>
    );
  }

  return (
    <div className={`ab-page ${isDarkMode ? 'dark-mode' : ''}`}>
      {reviewId ? (
        <ReviewView
          projectId={reviewId}
          onBack={() => setSearchParams({})}
        />
      ) : (
        <ListView
          pending={pending}
          all={all}
          onReview={(id) => setSearchParams({ review: id })}
        />
      )}
    </div>
  );
}

// ================================================================
// LIST
// ================================================================
function ListView({ pending, all, onReview }) {
  const stats = useMemo(() => ({
    total: all.length,
    pending: pending.length,
    published: all.filter((p) => p.state === PROJECT_STATE.PUBLISHED).length,
    building: all.filter((p) => p.state === PROJECT_STATE.BUILDING).length,
  }), [all, pending]);

  return (
    <>
<header className="ab-header">
  <div className="ab-header-inner">
    <div className="ab-header-id">
      <div className="ab-badge-icon">🎬</div>
      <div>
        <h1 className="ab-title">مدیریت مطالعه آزاد</h1>
        <div className="ab-subtitle">بررسی و تایید دوره‌های ویدیویی معلمان</div>
      </div>
    </div>
    <div style={{ display: 'flex', gap: 8 }}>
      <Link
        to="/admin/categories"
        className="ab-btn ab-btn-ghost"
        style={{ textDecoration: 'none' }}
      >
        📂 مدیریت دسته‌بندی‌ها
      </Link>
    </div>
  </div>

  <div className="ab-stats-grid">
          <StatCard icon="⏳" value={stats.pending} label="در انتظار بررسی" tone="warning" />
          <StatCard icon="✅" value={stats.published} label="منتشرشده" tone="success" />
          <StatCard icon="✏️" value={stats.building} label="در حال ساخت" tone="info" />
          <StatCard icon="📚" value={stats.total} label="کل دوره‌ها" tone="neutral" />
        </div>
      </header>

      <main className="ab-main" style={{ padding: 24 }}>
        {pending.length > 0 && (
          <section style={{ marginBottom: 32 }}>
            <h2 style={{ marginBottom: 16 }}>⏳ در انتظار بررسی ({pending.length})</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
              gap: 16,
            }}>
              {pending.map((p) => (
                <PendingCard key={p.id} project={p} onReview={() => onReview(p.id)} />
              ))}
            </div>
          </section>
        )}

        <section>
          <h2 style={{ marginBottom: 16 }}>📚 همه‌ی دوره‌ها ({all.length})</h2>
          {all.length === 0 ? (
            <EmptyState
              icon="🎬"
              title="هنوز دوره‌ای ساخته نشده"
              description="وقتی معلمان دوره بسازند، اینجا نمایش داده می‌شود."
            />
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
              gap: 16,
            }}>
              {all.map((p) => (
                <ProjectRow key={p.id} project={p} onReview={() => onReview(p.id)} />
              ))}
            </div>
          )}
        </section>
      </main>
    </>
  );
}

function StatCard({ icon, value, label, tone }) {
  return (
    <div className={`ab-stat-card ab-stat-${tone}`} style={{ cursor: 'default' }}>
      <div className="ab-stat-icon">{icon}</div>
      <div className="ab-stat-body">
        <div className="ab-stat-value">{value ?? 0}</div>
        <div className="ab-stat-label">{label}</div>
      </div>
    </div>
  );
}

function PendingCard({ project, onReview }) {
  const teacher = useTeacherProfile(project.teacherId);
  const version = project.pendingVersionId
    ? dataEngine.version.get(project.pendingVersionId)
    : null;
  const videoCount = version?.videos?.length || 0;
  const isUpdate = project.state === PROJECT_STATE.UPDATE_PENDING;

  return (
    <article style={{
      background: '#fff', borderRadius: 12, padding: 16,
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      border: '2px solid #fbbf24',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          background: '#e2e8f0', display: 'flex',
          alignItems: 'center', justifyContent: 'center', fontWeight: 700,
        }}>
          {teacher?.fullName?.charAt(0) || 'م'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{teacher?.fullName || 'معلم'}</div>
          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
            {isUpdate ? 'درخواست بروزرسانی' : 'درخواست اولیه'}
          </div>
        </div>
        <Badge tone="warning" size="sm">{isUpdate ? '🔄' : '⏳'}</Badge>
      </div>

      <h3 style={{ margin: '8px 0', fontSize: '1rem' }}>{project.title}</h3>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>🎬 {videoCount} ویدیو</span>
        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>💰 {formatPrice(project.fullCoursePrice, project.currency)}</span>
      </div>

      <button className="ab-btn ab-btn-primary" onClick={onReview} style={{ width: '100%' }}>
        👁️ بررسی و تصمیم
      </button>
    </article>
  );
}

function ProjectRow({ project, onReview }) {
  const teacher = useTeacherProfile(project.teacherId);
  const version = project.publishedVersionId
    ? dataEngine.version.get(project.publishedVersionId)
    : project.draftVersionId
      ? dataEngine.version.get(project.draftVersionId)
      : null;
  const videoCount = version?.videos?.length || 0;

  return (
    <article style={{
      background: '#fff', borderRadius: 12, padding: 16,
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
        <ProjectStateBadge state={project.state} size="sm" />
        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
          {teacher?.fullName || 'معلم'}
        </span>
      </div>
      <h3 style={{ margin: 0, fontSize: '1rem' }}>{project.title}</h3>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: '0.75rem', color: '#64748b' }}>
        <span>🎬 {videoCount}</span>
        <span>💰 {formatPrice(project.fullCoursePrice, project.currency)}</span>
      </div>
      <button className="ab-btn ab-btn-ghost" onClick={onReview} style={{ marginTop: 'auto' }}>
        👁️ مشاهده
      </button>
    </article>
  );
}

// ================================================================
// REVIEW VIEW
// ================================================================
function ReviewView({ projectId, onBack }) {
  const { toast } = useBooksEngine();
  const project = useMemo(() => dataEngine.project.get(projectId), [projectId]);
  const teacher = useTeacherProfile(project?.teacherId);

  const [selectedVersionId, setSelectedVersionId] = useState(null);

  useEffect(() => {
    if (!project) return;
    setSelectedVersionId(
      project.pendingVersionId ||
      project.draftVersionId ||
      project.publishedVersionId ||
      null
    );
  }, [project?.id]);

  const version = useMemo(
    () => (selectedVersionId ? dataEngine.version.get(selectedVersionId) : null),
    [selectedVersionId]
  );

  const { approveAndPublish, rejectPending, requestEdit } = useVersionActions();
  const { hardDelete } = useProjectActions();

  const [modal, setModal] = useState(null);
  const [reason, setReason] = useState('');

  const handleApprove = async () => {
    const res = await approveAndPublish.run(projectId, reason);
    if (res?.ok) {
      toast?.('دوره با موفقیت منتشر شد', 'success');
      setModal(null);
      setReason('');
      onBack();
    }
  };

  const handleReject = async () => {
    const res = await rejectPending.run(projectId, reason);
    if (res?.ok) {
      toast?.('دوره رد شد', 'success');
      setModal(null);
      setReason('');
      onBack();
    }
  };

  const handleRequestEdit = async () => {
    const res = await requestEdit.run(projectId, reason);
    if (res?.ok) {
      toast?.('درخواست اصلاح ارسال شد', 'success');
      setModal(null);
      setReason('');
      onBack();
    }
  };

  const handleDelete = () => {
    setModal(null);
    onBack();
    hardDelete.run(projectId);
  };

  if (!project) {
    return (
      <div style={{ padding: 40 }}>
        <EmptyState
          icon="📦"
          title="دوره یافت نشد"
          action={<button className="ab-btn ab-btn-primary" onClick={onBack}>بازگشت</button>}
        />
      </div>
    );
  }

  const isPending =
    project.state === PROJECT_STATE.PENDING_INITIAL ||
    project.state === PROJECT_STATE.UPDATE_PENDING;

  const videos = version?.videos || [];
  const totalMin = videos.reduce((s, v) => s + (Number(v.durationMinutes) || 0), 0);
  const coverUrl = resolveImageUrl(project.image || project.coverImageBlobId || '');

  return (
    <>
      <header className="ab-header">
        <div className="ab-header-inner">
          <div className="ab-header-id">
            <button className="ab-icon-btn" onClick={onBack} title="بازگشت">←</button>
            <div>
              <h1 className="ab-title">{project.title}</h1>
              <div className="ab-subtitle" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <ProjectStateBadge state={project.state} size="sm" />
                <span>👤 {teacher?.fullName || 'معلم'}</span>
                <span>📂 {project.category || 'بدون دسته'}</span>
              </div>
            </div>
          </div>
          <div className="ab-header-actions">
            {isPending && (
              <>
                <button className="ab-btn ab-btn-warning" onClick={() => setModal('edit')}>
                  ✏️ درخواست اصلاح
                </button>
                <button className="ab-btn ab-btn-danger" onClick={() => setModal('reject')}>
                  ✕ رد
                </button>
                <button className="ab-btn ab-btn-success" onClick={() => setModal('approve')}>
                  ✓ تایید و انتشار
                </button>
              </>
            )}
            {!project.archivedAt && (
              <button
                className="ab-btn ab-btn-ghost"
                onClick={() => setModal('delete')}
                style={{ color: '#dc2626' }}
              >
                🗑️ حذف
              </button>
            )}
          </div>
        </div>
      </header>

      <main style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
        {version?.teacherNote && (
          <div style={{
            padding: 14, background: '#e0f2fe', border: '1px solid #7dd3fc',
            borderRadius: 12, marginBottom: 20, color: '#0369a1',
          }}>
            <strong>📝 یادداشت معلم:</strong> {version.teacherNote}
          </div>
        )}

        <section style={{
          background: '#fff', borderRadius: 16, padding: 20,
          marginBottom: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {coverUrl && (
              <img
                src={coverUrl}
                alt="cover"
                style={{ width: 200, height: 112, objectFit: 'cover', borderRadius: 12 }}
              />
            )}
            <div style={{ flex: 1, minWidth: 200 }}>
              <h2 style={{ marginTop: 0 }}>{project.title}</h2>
              {project.subtitle && <p style={{ color: '#64748b', margin: '4px 0' }}>{project.subtitle}</p>}
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.9rem', marginTop: 8 }}>
                <span>🎬 {videos.length} ویدیو</span>
                <span>⏱️ {formatDuration(totalMin)}</span>
                <span>💰 {formatPrice(project.fullCoursePrice, project.currency)}</span>
                <span>🎯 {project.level || '—'}</span>
              </div>
            </div>
          </div>
          {project.description && (
            <p style={{ color: '#475569', marginTop: 16, lineHeight: 1.8 }}>{project.description}</p>
          )}
          {project.tags?.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
              {project.tags.map((t) => <Chip key={t} tone="purple">{t}</Chip>)}
            </div>
          )}
        </section>

        <section style={{
          background: '#fff', borderRadius: 16, padding: 20,
          marginBottom: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        }}>
          <h2 style={{ marginTop: 0 }}>🎬 ویدیوها ({videos.length})</h2>
          {videos.length === 0 ? (
            <EmptyState icon="🎬" title="ویدیویی وجود ندارد" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {videos.map((v, i) => (
                <div key={v._id} style={{
                  padding: 12, background: '#f8fafc',
                  borderRadius: 10, border: '1px solid #e2e8f0',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 700, color: '#64748b' }}>{toPersianDigits(i + 1)}</span>
                      <h4 style={{ margin: 0 }}>{v.title}</h4>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      {v.isFree && <Badge tone="success" size="sm">رایگان</Badge>}
                      {!v.isFree && v.price > 0 && (
                        <Badge tone="info" size="sm">{formatPrice(v.price, project.currency)}</Badge>
                      )}
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        ⏱️ {formatDuration(v.durationMinutes)}
                      </span>
                    </div>
                  </div>
                  {v.description && (
                    <p style={{ margin: '0 0 8px 0', fontSize: '0.85rem', color: '#64748b' }}>
                      {v.description}
                    </p>
                  )}
                  {v.blobId && (
                    <div style={{ maxWidth: 400 }}>
                      <VideoPreview blobId={v.blobId} title={v.title} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      <ConfirmModal
        isOpen={modal === 'approve'}
        onClose={() => setModal(null)}
        onConfirm={handleApprove}
        title="تایید و انتشار"
        message={`آیا از تایید و انتشار «${project.title}» مطمئن هستید؟ دوره برای همه‌ی دانش‌آموزان قابل مشاهده می‌شود.`}
        confirmText="✓ تایید و انتشار"
        tone="info"
        requireReason
        reasonLabel="یادداشت (اختیاری)"
        loading={approveAndPublish.loading}
      />

      <ConfirmModal
        isOpen={modal === 'reject'}
        onClose={() => setModal(null)}
        onConfirm={handleReject}
        title="رد درخواست"
        message="دلیل رد را بنویسید. این پیام به معلم ارسال می‌شود."
        confirmText="✕ رد"
        tone="danger"
        requireReason
        reasonLabel="دلیل رد"
        reasonRequired
        loading={rejectPending.loading}
      />

      <ConfirmModal
        isOpen={modal === 'edit'}
        onClose={() => setModal(null)}
        onConfirm={handleRequestEdit}
        title="درخواست اصلاح"
        message="چه چیزی باید اصلاح شود؟ این پیام به معلم ارسال می‌شود."
        confirmText="ارسال"
        tone="warning"
        requireReason
        reasonLabel="توضیح"
        reasonRequired
        loading={requestEdit.loading}
      />

      <ConfirmModal
        isOpen={modal === 'delete'}
        onClose={() => setModal(null)}
        onConfirm={handleDelete}
        title="🗑️ حذف کامل"
        message={`آیا از حذف دائمی «${project.title}» مطمئن هستید؟ تمام ویدیوها برای همیشه حذف می‌شوند.`}
        confirmText="🗑️ حذف دائمی"
        tone="danger"
        loading={hardDelete.loading}
      />
    </>
  );
}

function formatDuration(minutes) {
  if (!minutes || minutes <= 0) return '—';
  if (minutes < 60) return `${toPersianDigits(minutes)} دقیقه`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0
    ? `${toPersianDigits(h)} ساعت`
    : `${toPersianDigits(h)}:${String(m).padStart(2, '0')} ساعت`;
}